import type { ComponentProps, MouseEvent } from 'react';

import { Link as TanStackLink } from '@tanstack/react-router';

import {
    type TextLinkAnchorProps,
    type TextLinkButtonProps,
    type TextLinkProps,
    type TextLinkRouterProps,
} from './types';
import { cn } from '../../utils/cn';
import { handleEventBehavior } from '../../utils/EventBehavior';

/**
 * A styled text link component that supports TanStack Router navigation, anchor tags, and button variants.
 * Links are underlined and become bold on hover for accessibility compliance.
 *
 * @param children - The text content to display.
 * @param to - Route path for TanStack Router navigation (uses TanStack Router's Link component).
 * @param search - Search params for TanStack Router navigation (uses TanStack Router's Link component).
 * @param href - Optional href for navigation (renders as anchor tag, for external links or when router is not available).
 * @param onClick - Optional click handler (renders as button if to/href is not provided).
 * @param className - Additional CSS classes.
 * @param withUnderline - Whether to show an underline. Defaults to true.
 *
 * @example
 * <TextLink to="/dashboard">Dashboard</TextLink>
 *
 * @example
 * <TextLink href="/home">Go to Home</TextLink>
 *
 * @example
 * <TextLink onClick={() => console.log('clicked')}>Click me</TextLink>
 *
 * @example
 * <TextLink href="/home" withUnderline={false}>Go to Home</TextLink>
 */
export const TextLink: React.FC<TextLinkProps> = ({ children, className, withUnderline = true, ...props }) => {
    const baseClassName = cn(
        'text-sm leading-[1.46] text-midnight',
        withUnderline && 'underline',
        'font-normal',
        'transition-all',
        className,
    );

    // Check if using TanStack Router
    if ('to' in props && props.to !== undefined) {
        const { activeOptions, activeProps, inactiveProps, params, search, to, ...routerProps } =
            props as TextLinkRouterProps;
        return (
            <TanStackLink
                to={to}
                params={params}
                search={search as ComponentProps<typeof TanStackLink>['search']}
                activeProps={activeProps}
                inactiveProps={inactiveProps}
                activeOptions={activeOptions}
                {...routerProps}
                className={baseClassName}
            >
                {children}
            </TanStackLink>
        );
    }

    // Regular anchor tag
    if ('href' in props && props.href !== undefined) {
        const { href, ...anchorProps } = props as TextLinkAnchorProps;
        return (
            <a href={href} {...anchorProps} className={baseClassName}>
                {children}
            </a>
        );
    }

    // Button
    const {
        eventPreventDefault,
        eventStopPropagation,
        onClick,
        type = 'button',
        ...buttonProps
    } = props as TextLinkButtonProps;
    return (
        <button
            type={type}
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
                handleEventBehavior(e, { eventPreventDefault, eventStopPropagation });
                onClick?.();
            }}
            {...buttonProps}
            className={baseClassName}
        >
            {children}
        </button>
    );
};

TextLink.displayName = 'TextLink';
